// Notifikationer: hvem får besked om hvad. Bruges både af testversionen og af serveren (Supabase edge functions),
// så reglerne er de samme alle steder. Selve udsendelsen sker som push til telefonen/computeren (PWA) og
// i klokken i appen. Kunder får ikke push – de får SMS og mail som hidtil.
import { har, type Evne, type RolleEvner } from './roller.ts';
import type { Fravaer, Rolle } from './typer.ts';

export type NotifType =
  | 'ny_opgave' | 'opgave_flyttet' | 'opgave_fjernet' | 'ekstra_svar' | 'fastfri_svar' | 'fravaer_svar' // steameren
  | 'afventer_godkendelse' | 'skal_rykkes' | 'mangler_tid' | 'ekstra_solgt' // kontoret
  | 'kundesag' | 'udlaeg_ny'                                     // kontoret
  | 'ny_booking' | 'booking_aflyst' | 'booking_flyttet' | 'fravaer_anmodning' // kontoret
  | 'udlaeg_status'                                                         // den, der har lagt ud
  | 'medarbejder_data'                                                      // ejer/admin: en steamer har udfyldt løn/ID
  | 'rente_tjek'                                                            // ejer/admin: tjek referencesatsen 1/1 og 1/7 (kan ikke slås fra)
  | 'team_opgave'                                                           // ejer/admin: invitation/oplysninger/kørekort (core/invitation.ts; kan ikke slås fra)
  | 'chat'                                                                  // alle i samtalen
  | 'test';                                                                 // "Send en test" fra indstillingerne

type Modtager = { rolle: Exclude<Rolle, 'kunde'> };
export type NotifTypeInfo = {
  id: NotifType; navn: string; beskrivelse: string;
  /** Kan rollen overhovedet få denne type? (Så indstillingerne kun viser det relevante.) */
  for: (r: Exclude<Rolle, 'kunde'>, m: RolleEvner) => boolean;
};

const kontor = (evne: Evne) => (r: Exclude<Rolle, 'kunde'>, m: RolleEvner) => r !== 'steamer' && har(r, evne, m);
const steamer = (r: Exclude<Rolle, 'kunde'>) => r === 'steamer';

export const NOTIF_TYPER: NotifTypeInfo[] = [
  { id: 'ny_opgave', navn: 'Ny opgave', beskrivelse: 'Når du får en ny booking i din plan', for: steamer },
  { id: 'opgave_flyttet', navn: 'Opgave flyttet', beskrivelse: 'Når tiden på en af dine opgaver bliver ændret', for: steamer },
  { id: 'opgave_fjernet', navn: 'Opgave fjernet', beskrivelse: 'Når en opgave bliver aflyst eller givet til en anden', for: steamer },
  { id: 'ekstra_svar', navn: 'Svar på ekstra tilvalg', beskrivelse: 'Når kunden godkender eller afviser det, du har tilføjet', for: steamer },
  { id: 'fastfri_svar', navn: 'Svar på fast fri', beskrivelse: 'Når kontoret godkender eller afviser din anmodning om fast fri', for: steamer },
  { id: 'fravaer_svar', navn: 'Svar på fravær', beskrivelse: 'Når kontoret godkender eller afviser din anmodning om fri, sygdom eller ferie', for: steamer },
  { id: 'afventer_godkendelse', navn: 'Booking venter på godkendelse', beskrivelse: 'Når en kunde bestiller, og bookingen skal godkendes', for: kontor('bookinger.godkend') },
  { id: 'ny_booking', navn: 'Ny booking', beskrivelse: 'Når der kommer en ny booking – fra hjemmesiden, en kunde eller en kollega', for: kontor('bookinger.se') },
  { id: 'booking_aflyst', navn: 'Booking aflyst', beskrivelse: 'Når en booking bliver aflyst', for: kontor('bookinger.se') },
  { id: 'booking_flyttet', navn: 'Booking flyttet', beskrivelse: 'Når en kunde eller steamer flytter en booking til en ny tid', for: kontor('bookinger.se') },
  { id: 'fravaer_anmodning', navn: 'Fravær til godkendelse', beskrivelse: 'Når en steamer beder om fri, ferie, fast fri eller melder sig syg', for: kontor('fravaer.meld') },
  { id: 'skal_rykkes', navn: 'Booking skal rykkes', beskrivelse: 'Når en opgave mister sin steamer (fx sygdom) og skal have ny tid', for: kontor('bookinger.ret') },
  { id: 'mangler_tid', navn: 'Kunde mangler ny tid', beskrivelse: 'Når en kunde, der ville vende tilbage med en ny tid, endnu ikke har valgt en', for: kontor('bookinger.ret') },
  { id: 'ekstra_solgt', navn: 'Ekstra salg', beskrivelse: 'Når en medarbejder har solgt et ekstra tilvalg, og kunden har sagt ja', for: kontor('mersalg.se') },
  { id: 'kundesag', navn: 'Ny kundesag', beskrivelse: 'Når en kunde opretter en sag i Kundecenter', for: kontor('kundecenter.se') },
  { id: 'udlaeg_ny', navn: 'Udlæg til godkendelse', beskrivelse: 'Når en medarbejder sender et udlæg', for: kontor('udlaeg.godkend') },
  { id: 'udlaeg_status', navn: 'Mine udlæg', beskrivelse: 'Når dit udlæg bliver godkendt, afvist, udbetalt eller der mangler noget', for: () => true },
  { id: 'medarbejder_data', navn: 'Steamernes oplysninger', beskrivelse: 'Når en steamer har udfyldt CPR, bankkonto eller uploadet ID', for: (r) => r === 'ejer' || r === 'admin' },
  { id: 'chat', navn: 'Chatbeskeder', beskrivelse: 'Når nogen skriver til dig i chatten', for: (r, m) => r === 'steamer' || har(r, 'chat.brug', m) },
];
export const notifInfo = (t: NotifType): NotifTypeInfo | undefined => NOTIF_TYPER.find((x) => x.id === t);

/** Typer, en bruger med denne rolle kan vælge til og fra. */
export function typerFor(m: Modtager, matrix: RolleEvner): NotifTypeInfo[] {
  return NOTIF_TYPER.filter((t) => t.for(m.rolle, matrix));
}

/** Får brugeren denne type? Rollen skal kunne få den, og brugeren må ikke have slået den fra. */
export function faarType(m: Modtager, type: NotifType, matrix: RolleEvner, fra: readonly NotifType[] = []): boolean {
  const i = notifInfo(type);
  if (!i) return true; // test-notifikationen
  return i.for(m.rolle, matrix) && !fra.includes(type);
}

/** En notifikation, før den er sendt til en bestemt person. */
export type NotifKladde = { profil_id: string; type: NotifType; titel: string; tekst: string; link: string; booking_id?: number | null };

/**
 * Samler mange notifikationer af samme slags til samme person i én (fx når en hel dag bliver ruteoptimeret,
 * eller en syg steamer mister 6 opgaver). Højst 2 enkeltstående af samme slags – resten bliver til én samlet.
 */
export function samlNotif(k: NotifKladde[]): NotifKladde[] {
  const grupper = new Map<string, NotifKladde[]>();
  for (const n of k) {
    const noegle = n.profil_id + '|' + n.type + (n.type === 'chat' ? '|' + n.link : '');
    grupper.set(noegle, [...(grupper.get(noegle) ?? []), n]);
  }
  const ud: NotifKladde[] = [];
  for (const g of grupper.values()) {
    if (g.length <= 2) { ud.push(...g); continue; }
    const f = g[0];
    ud.push({ profil_id: f.profil_id, type: f.type, link: f.type === 'chat' ? f.link : SAMLET_LINK[f.type] ?? f.link, titel: SAMLET_TITEL[f.type]?.(g.length) ?? f.titel,
      tekst: f.type === 'chat' ? g[g.length - 1].tekst : g.slice(0, 3).map((x) => x.tekst.split('\n')[0]).join(' · ') + (g.length > 3 ? ' …' : ''), booking_id: null });
  }
  return ud;
}
const SAMLET_TITEL: Partial<Record<NotifType, (n: number) => string>> = {
  ny_opgave: (n) => `${n} nye opgaver`, opgave_flyttet: (n) => `${n} opgaver er flyttet`, opgave_fjernet: (n) => `${n} opgaver er fjernet fra din plan`,
  afventer_godkendelse: (n) => `${n} bookinger venter på godkendelse`, skal_rykkes: (n) => `${n} bookinger skal rykkes`, mangler_tid: (n) => `${n} kunder mangler en ny tid`,
  ekstra_solgt: (n) => `${n} ekstra salg`, kundesag: (n) => `${n} nye kundesager`,
  udlaeg_ny: (n) => `${n} udlæg venter`, chat: (n) => `${n} nye beskeder`,
  ny_booking: (n) => `${n} nye bookinger`, booking_aflyst: (n) => `${n} bookinger er aflyst`, booking_flyttet: (n) => `${n} bookinger er flyttet`,
  fravaer_anmodning: (n) => `${n} anmodninger om fravær`,
};
const SAMLET_LINK: Partial<Record<NotifType, string>> = {
  ny_opgave: '/steamer/kalender', opgave_flyttet: '/steamer/kalender', opgave_fjernet: '/steamer/kalender',
  afventer_godkendelse: '/admin/afventer', skal_rykkes: '/admin/bookinger', mangler_tid: '/admin/overblik', ekstra_solgt: '/admin/mersalg', kundesag: '/admin/kundecenter',
  udlaeg_ny: '/admin/udlaeg', ny_booking: '/admin/bookinger', booking_aflyst: '/admin/bookinger', booking_flyttet: '/admin/bookinger', fravaer_anmodning: '/admin/steamere',
};

/** Typer, der giver kontoret en "pling", når appen er åben (og push med lyd, når den er lukket). */
export const PLING_TYPER: readonly NotifType[] = ['ny_booking', 'booking_aflyst', 'booking_flyttet', 'afventer_godkendelse', 'fravaer_anmodning', 'chat', 'kundesag', 'skal_rykkes', 'test',
  'ny_opgave', 'opgave_flyttet', 'opgave_fjernet', 'ekstra_svar'];

/** Kort uddrag af en chatbesked til en notifikation. */
export function chatUddrag(tekst: string, vedhaeft?: { type: string } | null): string {
  if (tekst.trim()) return tekst.trim().replace(/\s+/g, ' ').slice(0, 120) + (tekst.trim().length > 120 ? ' …' : '');
  const t = vedhaeft?.type ?? '';
  return t.startsWith('image/') ? '📷 Billede' : t.startsWith('video/') ? '🎬 Video' : t.startsWith('audio/') ? '🎤 Talebesked' : '📎 Fil';
}


const DAGE = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
const MDR = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];
const dagKort = (dato: string, medDag: boolean) => {
  const [y, m, d] = dato.split('-').map(Number);
  return (medDag ? DAGE[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] + ' ' : '') + d + '. ' + MDR[m - 1];
};
/** Steamerens besked, når kontoret svarer på fri/syg/ferie: "Dit fravær torsdag 30. okt. er godkendt" (som svar_fravaer i databasen). */
export function fravaerSvarBesked(a: Pick<Fravaer, 'fra' | 'til' | 'type'>, godkend: boolean, grund?: string | null): { titel: string; tekst: string } {
  const periode = a.fra === a.til ? dagKort(a.fra, true) : `${dagKort(a.fra, false)} – ${dagKort(a.til, false)}`;
  const type = ({ syg: 'Syg', ferie: 'Ferie' } as Record<string, string>)[a.type] ?? 'Fri';
  return { titel: `Dit fravær ${periode} er ${godkend ? 'godkendt' : 'afvist'}`, tekst: type + (grund ? `\n${grund}` : '') };
}
